import { useState, Fragment, useRef, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
// import { Field } from "formik";
// import { ArrowDown, ArrowUp, Snowflake, Zap } from "lucide-react";
import LoadingOverlay from "../../helper-components/overlay-loading";
import errorToast from "../../helper-components/error-toast";
import CollapsibleFilter from "../../helper-components/collapsible-box";
import { useHotkeys } from "../../helper-components/use-hotkeys";
import { useDispatch } from "react-redux";
import { binLocationListAction } from "../../../redux/actions/general-action";
import successToast from "../../helper-components/success-toast";
import {
  purchaseExpiryBinAction,
  purchaseSlowNonMovingItemsAction,
} from "../../../redux/actions/purchase-action";
import { useFormikContext } from "formik";
import InputField from "../../forms/input-field";
import SelectField from "../../forms/select-field";
import DatePickerInput from "../../forms/date-picker";

const FILTER_FIELD_LABEL_CLASS =
  "text-sm font-medium text-[var(--nav-heading-color)]";
const FILTER_FIELD_INPUT_CLASS = "rounded-md px-3 py-2 text-sm bg-white";
const FILTER_FIELD_BORDER_CLASS = "border border-[var(--button-border-color)]";
const FILTER_FIELDS = [
  "prt_filter_bin_id",
  "prt_filter_age_days",
  "prt_filter_from_date",
  "prt_filter_to_date",
  "prt_filter_movement_date",
];

// react-datepicker draws its own calendar popup instead of relying on the
// browser/webview's native <input type="date"> chrome, which doesn't render
// consistently (icon/placeholder can silently disappear) in some webviews.
const FilterDateField = ({
  name,
  label,
  isRequired = false,
  value,
  onChange,
  maxDate,
  minDate,
}) => (
  <div className="flex flex-col gap-1 w-full">
    <label className={FILTER_FIELD_LABEL_CLASS}>
      {label} {isRequired && <span className="text-F51D00">*</span>}
    </label>
    <DatePickerInput
      value={value || ""}
      onChange={(val) => onChange(name, val)}
      placeholder={label}
      width="100%"
      height="42px"
      borderClass="border-[var(--button-border-color)]"
      className="w-full bg-transparent focus:outline-none text-sm"
      maxDate={maxDate}
      minDate={minDate}
      portalId="purchase-return-filter-datepicker-portal"
      iconPosition="right"
    />
  </div>
);

const PurchaseReturnSidePanel = ({ open, setOpen }) => {
  const [loading, setLoading] = useState(false);
  const close=useRef(null);
  const headerRefs = useRef([]);
  const dispatch = useDispatch();
  const { values, setFieldValue } = useFormikContext();
  const [binOptions, setBinOptions] = useState([]);
  const [dateModeDays, setDateModeDays] = useState(true); // true => Days, false => Calendar

  // Seed the filter fields into Formik once so InputField/SelectField (which
  // read/write via Formik's Field) start out controlled instead of undefined.
  useEffect(() => {
    FILTER_FIELDS.forEach((field) => {
      if (values?.[field] === undefined) setFieldValue(field, "", false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
//   useHotkeys("1,2", (_, handler) => {
//     const key = handler.keys.join(""); 
//     const match = key.match(/^[12]$/);
//     if (!match) return;
//     const index = Number(match[0]) - 1;
//     headerRefs.current[index]?.click();
//   });
  useHotkeys("1", () => {
    headerRefs.current[0]?.click();
  });
  useHotkeys("2", () => {
    headerRefs.current[1]?.click();
  });
  useHotkeys("esc", () => {
        if (close.current) {
            close.current.click();
        }
    });

  // Validate cmr_id and load bins on open
  useEffect(() => {
    if (open) {
      if (!values?.cmr_id) {
        errorToast("Please select a vendor before loading items");
        setOpen(false);
        return;
      }
      dispatch(
        binLocationListAction(
          (res) => {
            const opts =
              res?.data?.map((b) => ({
                label: b?.bin_location_no,
                value: b?.bin_no_id,
                raw: b,
              })) ?? [];
            setBinOptions(opts);
          },
          () => {}
        )
      );
    } else {
      setDateModeDays(true);
      FILTER_FIELDS.forEach((field) => setFieldValue(field, "", false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dispatch, setOpen, values?.cmr_id]);

  // The movement API can report the status on the item or repeat it on each
  // batch, so read whichever one is present.
  const readMovementStatus = (it, batchKey) =>
    it?.movement_status ||
    (it?.[batchKey] || []).find((b) => b?.movement_status)?.movement_status ||
    "";

  const transformApiItem = (it, batchKey, sl_no, withMovementStatus = false) => ({
    sl_no,
    ...(withMovementStatus
      ? { movement_status: readMovementStatus(it, batchKey) }
      : {}),
    item_name: it?.item_name,
    item_generic_name: it?.item_generic_name ?? "",
    item_code: it?.item_code,
    item_hsn: it?.item_hsn,
    item_manufacturer_name: it?.item_manufacturer_name ?? "",
    item_uom: it?.item_uom,
    item_batches:
      (it?.[batchKey] || []).map((b) => ({
        item_batch_number: b?.item_batch_number,
        item_sellable_quantity: Number(b?.item_sellable_quantity) || 0,
        item_batch_free_quantity: Number(b?.item_batch_free_quantity) || 0,
        item_batch_total_effective_qauntity:
          Number(b?.item_batch_total_effective_qauntity) || 0,
        item_batch_total_base_purchase_rate:
          Number(b?.item_batch_total_base_purchase_rate) || 0,
        item_batch_for_qauntity: Number(b?.item_batch_for_qauntity) || 0,
        item_batch_for_free_qauntity:
          Number(b?.item_batch_for_free_qauntity) || 0,
        item_batch_scheme_discount_percentage:
          Number(b?.item_batch_scheme_discount_percentage) || 0,
        item_batch_scheme_discount_amount:
          Number(b?.item_batch_scheme_discount_amount) || 0,
        item_batch_scheme_purchase_rate:
          Number(b?.item_batch_scheme_purchase_rate) || 0,
        item_batch_discount_percentage:
          Number(b?.item_batch_discount_percentage) || 0,
        item_batch_gen_discount_amount:
          Number(b?.item_batch_gen_discount_amount) || 0,
        item_batch_discounted_purchase_rate:
          Number(b?.item_batch_discounted_purchase_rate) || 0,
        item_batch_purchase_rate: Number(b?.item_batch_purchase_rate) || 0,
        item_batch_discount_amount: Number(b?.item_batch_discount_amount) || 0,
        item_batch_tax_percentage: Number(b?.item_batch_tax_percentage) || 0,
        //alter likith
        item_batch_tax_amount: Number(b?.item_batch_tax_amount) || 0,
        item_batch_cgst_percentage: Number(b?.item_batch_cgst_percentage) || 0,
        item_batch_sgst_percentage: Number(b?.item_batch_sgst_percentage) || 0,
        item_batch_igst_percentage: Number(b?.item_batch_igst_percentage) || 0,
        item_batch_cgst_amount: Number(b?.item_batch_cgst_amount) || 0,
        item_batch_sgst_amount: Number(b?.item_batch_sgst_amount) || 0,
        item_batch_igst_amount: Number(b?.item_batch_igst_amount) || 0,
        item_batch_final_purchase_rate:
          Number(b?.item_batch_final_purchase_rate) || 0,
        item_batch_total_purchase_rate:
          Number(b?.item_batch_total_purchase_rate) || 0,
        item_batch_unit_price: Number(b?.item_batch_unit_price) || 0,
        item_exp_date: b?.item_exp_date,
        item_id: b?.item_id,
        to_bin_location: b?.to_bin_location,
        to_bin_id: b?.to_bin_id,
      })) || [],
    item_return_open_quantity: 0,
    quantity: Number(it?.item_quantity) || 0,
    item_pack_size: 0,
    base_quantity: Number(it?.base_quantity) || 0,
    item_free_quantity: Number(it?.item_free_quantity) || 0,
    item_discount_amount: Number(it?.item_discount_amount) || 0,
    item_tax_amount: Number(it?.item_tax_amount) || 0,
    item_total_amount: Number(it?.item_total_amount) || 0,
    item_id: it?.item_id,
    pot_id: it?.pot_id ?? null,
    gort_id: it?.gort_id ?? null,
    item_gst: Number(it?.item_gst) || 0,
    item_sgst: Number(it?.item_sgst) || 0,
    item_cgst: Number(it?.item_cgst) || 0,
    item_igst: Number(it?.item_igst) || 0,
    item_uom_id: it?.item_uom_id,
    item_manufacturer_id: it?.item_manufacturer_id ?? "",
  });

  const setTotalsFromItems = (items) => {
    const totals = items.reduce(
      (acc, it) => {
        acc.totalAmount+=Number(it.item_total_amount)||0
        acc.subTotal += Number(it.item_total_amount-it.item_tax_amount) || 0;
        acc.tax += Number(it.item_tax_amount) || 0;
        acc.discount += Number(it.item_discount_amount) || 0;
        return acc;
      },
      { subTotal: 0, tax: 0, discount: 0 ,totalAmount:0}
    );
    setFieldValue("prt_sub_total", totals.subTotal);
    setFieldValue("prt_total_gst", totals.tax);
    setFieldValue("prt_total_discount", totals.discount);
    setFieldValue("prt_total_amount", totals.totalAmount);
  };

  const applyItemsToForm = (apiItems) => {
    const transformed = (apiItems || []).map((it, idx) =>
      transformApiItem(it, "itemBatchData", idx + 1)
    );
    const tail =
      Array.isArray(values?.data) && values.data.length > 0
        ? [values.data[values.data.length - 1]]
        : [];
    setFieldValue("data", [...transformed, ...tail]);
    setTotalsFromItems(transformed);
  };

  const applyMovementItemsToForm = (apiItems) => {
    const existingReal =
      Array.isArray(values?.data) && values.data.length > 0
        ? values.data.slice(0, -1)
        : [];
    const tail =
      Array.isArray(values?.data) && values.data.length > 0
        ? [values.data[values.data.length - 1]]
        : [];
    const transformed = (apiItems || []).map((it, idx) =>
      transformApiItem(it, "itemBatchData", existingReal.length + idx + 1, true)
    );
    const combined = [...existingReal, ...transformed];
    setFieldValue("data", [...combined, ...tail]);
    setTotalsFromItems(combined);
    setOpen(false);
  };

  const handleLoad = () => {
    const selectedBin = values?.prt_filter_bin_id;
    const ageDays = values?.prt_filter_age_days;
    const fromDate = values?.prt_filter_from_date;
    const toDate = values?.prt_filter_to_date;
    if (!selectedBin) {
      errorToast("Please select a bin location");
      return;
    }
    if (!values?.cmr_id) {
      errorToast("Please select a vendor");
      return;
    }
    const body = dateModeDays
      ? {
          cmr_id: values?.cmr_id,
          bin_id: selectedBin,
          age: Number(ageDays || 0),
        }
      : {
          cmr_id: values?.cmr_id,
          bin_id: selectedBin,
          from: fromDate,
          to: toDate,
        };
    if (dateModeDays && (!ageDays || Number(ageDays) <= 0)) {
      errorToast("Please enter product age (days)");
      return;
    }
    if (!dateModeDays && (!fromDate || !toDate)) {
      errorToast("Please select both From date and To date");
      return;
    }
    setLoading(true);
    dispatch(
      purchaseExpiryBinAction(
        body,
        (resp) => {
          setLoading(false);
          successToast(resp?.message || "Items loaded");
          applyItemsToForm(resp?.data?.itemData || []);
          setOpen(false);
          // keep panel open for further loads
        },
        (err) => {
          setLoading(false);
          errorToast(err?.data?.message || err?.message ||"Failed to load");
        }
      )
    );
  };

  const handleLoadMovementItems = () => {
    const movementDate = values?.prt_filter_movement_date;
    if (!values?.cmr_id) {
      errorToast("Please select a vendor");
      return;
    }
    if (!movementDate) {
      errorToast("Please select a date");
      return;
    }
    setLoading(true);
    dispatch(
      purchaseSlowNonMovingItemsAction(
        { cmr_id: values?.cmr_id, date: movementDate },
        (resp) => {
          setLoading(false);
          successToast(resp?.message || "Items loaded");
          applyMovementItemsToForm(resp?.data?.itemData || []);
        },
        (err) => {
          setLoading(false);
          errorToast(err?.data?.message || err?.message || "Failed to load");
        }
      )
    );
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => setOpen(false)}
      >
        <div className="fixed inset-0 overflow-hidden">
          {/* Background Overlay */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
          </Transition.Child>

          {/* Slide-in Panel */}
          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="h-full relative w-[400px] sm:w-[600px] bg-white rounded-tl-lg rounded-bl-lg shadow-xl flex flex-col">
                
                {/* Header */}
                <div className="p-4 flex justify-between items-center border-b border-[var(--button-border-color)]">
                  <div>
                    <h2 className="text-lg font-semibold">Select Return Type</h2>
                    <div className="text-sm pt-1 text-[var(--description-color)]">
                      Select option from Below List
                    </div>
                  </div>
                  <button
                    ref={close}
                    onClick={() => setOpen(false)}
                  >
                    ✕
                  </button>
                </div>

                {/* Scrollable Content */}
                <div className="space-y-4 p-4 flex-1 overflow-y-auto">
                    <CollapsibleFilter
                        headerText="Load Bin Location"
                        className="w-full max-w-full"
                        headerButtonRef={(el) => (headerRefs.current[0] = el)}
                    >
                      <div className="flex flex-col gap-4">
                        <SelectField
                          name="prt_filter_bin_id"
                          label="Select Bin Loaction"
                          isRequired={true}
                          options={binOptions}
                          placeholder="Select Bin"
                          containerClass=""
                          parentClass="flex flex-col gap-1 w-full"
                          labelClass={FILTER_FIELD_LABEL_CLASS}
                          defaultInputClass={FILTER_FIELD_INPUT_CLASS}
                          inputBorder={FILTER_FIELD_BORDER_CLASS}
                          errMsgContainerClass="hidden"
                          labelWidth="auto"
                        />
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-[var(--nav-heading-color)]">
                            Select Date Format
                          </span>
                          <div className="flex items-center gap-2 text-sm text-[var(--description-color)]">
                            <span>Calendar</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={dateModeDays}
                                onChange={(e) => setDateModeDays(e.target.checked)}
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-primary transition-colors"></div>
                              <span className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></span>
                            </label>
                            <span className="font-semibold">Days</span>
                          </div>
                        </div>
                        {dateModeDays ? (
                          <InputField
                            name="prt_filter_age_days"
                            label="Product Age"
                            type="number"
                            placeholder="Enter Product Age"
                            min={1}
                            containerClass=""
                            parentClass="flex flex-col gap-1 w-full"
                            labelClass={FILTER_FIELD_LABEL_CLASS}
                            defaultInputClass={FILTER_FIELD_INPUT_CLASS}
                            inputBorder={FILTER_FIELD_BORDER_CLASS}
                            errMsgContainerClass="hidden"
                            labelWidth="auto"
                          />
                        ) : (
                          <div className="grid grid-cols-2 gap-3">
                            <FilterDateField
                              name="prt_filter_from_date"
                              label="From Date"
                              value={values?.prt_filter_from_date}
                              onChange={setFieldValue}
                              maxDate={values?.prt_filter_to_date}
                            />
                            <FilterDateField
                              name="prt_filter_to_date"
                              label="To Date"
                              value={values?.prt_filter_to_date}
                              onChange={setFieldValue}
                              minDate={values?.prt_filter_from_date}
                            />
                          </div>
                        )}
                        <div className="flex justify-end">
                          <button
                            type="button"
                            className="bg-primary text-white px-5 py-2 rounded-md text-sm"
                            onClick={handleLoad}
                          >
                            Load
                          </button>
                        </div>
                      </div>
                    </CollapsibleFilter>
                    <CollapsibleFilter
                        headerText="Load items based on the movement"
                        className="w-full max-w-full"
                        headerButtonRef={(el) => (headerRefs.current[1] = el)}
                    >
                        <div className="flex flex-col gap-4">
                          <FilterDateField
                            name="prt_filter_movement_date"
                            label="Select Date"
                            isRequired={true}
                            value={values?.prt_filter_movement_date}
                            onChange={setFieldValue}
                          />
                          <div className="flex justify-end">
                            <button
                              type="button"
                              className="bg-primary text-white px-5 py-2 rounded-md text-sm"
                              onClick={handleLoadMovementItems}
                            >
                              OK
                            </button>
                          </div>
                        </div>
                    </CollapsibleFilter>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-[var(--button-border-color)] bg-white sticky bottom-0 flex justify-between">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="bg-[#ff0000] text-white px-6 py-2 rounded-md fw_600 hover:opacity-90"
                  >
                    Cancel
                  </button>
                  <div />
                </div>

                {/* Loading Overlay */}
                <LoadingOverlay isLoading={loading} />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default PurchaseReturnSidePanel;
